<script setup lang="ts">
const props = defineProps<{
  item: TStoreItem;
}>();

const emit = defineEmits<{
  (e: 'select-item', item: TStoreItem): void;
}>();

function parseNumericVal(v: any): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    const match = s.match(/-?\d+(?:[.,]\d+)?/);

    if (!match) return undefined;

    const normalized = match[0].replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);

    return Number.isFinite(n) ? n : undefined;
  }

  return undefined;
}

const originalPrice = computed<number | undefined>(() => {
  const val = (props.item as any)?.purchase_points_amount ?? props.item.price;

  return typeof val === 'number' ? val : undefined;
});

const discountedPrice = computed<number | undefined>(() => {
  const raw = (props.item as any)?.discounted_price;

  return parseNumericVal(raw);
});

const hasDiscount = computed(
  () =>
    typeof discountedPrice.value === 'number' &&
    (discountedPrice.value as number) > 0,
);

type TCurrency = 'points' | 'diamonds' | 'gems';

const itemCurrency = computed<TCurrency>(() => {
  const pt = String((props.item as any)?.purchase_type || '').toLowerCase();

  if (pt === 'points' || pt === 'diamonds' || pt === 'gems') {
    return pt as TCurrency;
  }

  const st = String((props.item as any)?.spanType || '').toLowerCase();

  if (
    typeof (props.item as any)?.price_diamonds === 'number' ||
    st.includes('diamonds')
  ) {
    return 'diamonds';
  }
  if (
    typeof (props.item as any)?.price_gems === 'number' ||
    st.includes('gem')
  ) {
    return 'gems';
  }

  return 'points';
});

const currencyIcon = computed(() => {
  if (itemCurrency.value === 'diamonds') return 'currency-header/diamond';
  if (itemCurrency.value === 'gems') return 'currency-header/gem';

  return 'currency-header/coin';
});

const currencyLabel = computed(() => {
  if (itemCurrency.value === 'diamonds') return 'DIAMONDS';
  if (itemCurrency.value === 'gems') return 'GEMS';

  return 'COINS';
});

const currencyColor = computed(() => {
  if (itemCurrency.value === 'diamonds') return '#00C9FF';
  if (itemCurrency.value === 'gems') return '#81FFA1';

  return '#FF9B50';
});

const canBuy = computed(() => {
  const pool = (props.item as any)?.pool;
  const poolOk = typeof pool !== 'number' || pool > 0;

  return !!props.item.can_buy && poolOk;
});

function handleBuy() {
  emit('select-item', props.item);
}
</script>

<template>
  <div
    class="store-card"
    @click="handleBuy"
  >
    <!-- Header: Imagem + Info -->
    <div class="store-card__header">
      <SmarticoApostatudoSmarticoImage
        :alt="item.name"
        class="store-card__image"
        :src="item.image"
      />
      <div class="store-card__info">
        <div class="store-card__title">
          {{ item.name }}
        </div>
        <div class="store-card__description">
          {{ item.description }}
        </div>
      </div>
    </div>

    <!-- Preço -->
    <div class="store-card__price">
      <NuxtIcon
        class="store-card__price-icon"
        :filled="true"
        :name="currencyIcon"
      />
      <div class="store-card__price-values">
        <!-- Preço com desconto (riscado) -->
        <span
          v-if="hasDiscount"
          class="store-card__price-old"
        >
          {{ discountedPrice?.toLocaleString('pt-BR') }}
        </span>
        <!-- Preço atual -->
        <span
          class="store-card__price-current"
          :style="{ color: currencyColor }"
        >
          {{ originalPrice?.toLocaleString('pt-BR') || '0' }}
          {{ currencyLabel }}
        </span>
      </div>
    </div>

    <!-- Botão -->
    <button
      class="store-card__button"
      :class="{ 'store-card__button--disabled': !canBuy }"
      :disabled="!canBuy"
      @click.stop="handleBuy"
    >
      <span>COMPRAR</span>
      <NuxtIcon
        :filled="true"
        name="store/cart2"
      />
    </button>
  </div>
</template>

<style scoped>
.store-card {
  width: 212px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  user-select: none;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;
  overflow: hidden;
}

.store-card:hover {
  border-color: #2d3339;
}

/* Header: Imagem + Info */
.store-card__header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-shrink: 0;
}

.store-card__image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: cover;
}

.store-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.store-card__title {
  font-size: 12px;
  font-weight: 700;
  color: white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.25;
}

.store-card__description {
  font-size: 10px;
  font-weight: 400;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.3;
}

/* Preço */
.store-card__price {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(0, 201, 255, 0.1);
  border-radius: 8px;
  flex-shrink: 0;
  margin: auto 0;
}

.store-card__price-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.store-card__price-values {
  display: flex;
  align-items: center;
  gap: 6px;
}

.store-card__price-old {
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: line-through;
}

.store-card__price-current {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

/* Botão */
.store-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 8px;
  background: var(--accent-color, #1414dd);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 0;
}

.store-card__button:hover:not(:disabled) {
  filter: brightness(1.1);
}

.store-card__button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
